/**
 * The playbooks' reads (vNext-api `docs/playbooks.md`): the list polls on
 * its ETag; a playbook and a version are read when the page opens and
 * after a publish (`refetch`).
 */

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type { PlaybookDetail, PlaybookSummary, PlaybookVersion } from '@/types/playbook';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll, withEtag } from './usePoll';

export const PLAYBOOKS_POLL_MS = 30_000;

export interface Read<T> {
  data: T | null;
  error: Error | null;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

function errorOf(error: unknown): Error | null {
  return error === undefined ? null : toError(error);
}

/** Every playbook — the house, then each client. */
export function usePlaybooks(): Read<PlaybookSummary[]> {
  const poll = usePoll<PlaybookSummary[]>({
    interval: PLAYBOOKS_POLL_MS,
    queryFn: async ({ signal, etag, manual }) => {
      const result = await vnextApiClient.listPlaybooks({ signal, etag: manual ? null : etag });
      return result === 'not-modified' ? UNCHANGED : withEtag(result.data, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });
  return {
    data: poll.data ?? null,
    error: errorOf(poll.error),
    isLoading: poll.isLoading,
    refetch: poll.refetch,
  };
}

/** One read when the key is set, again on `refetch`. */
function useRead<T>(key: string, enabled: boolean, fetch: () => Promise<T>): Read<T> {
  const poll = usePoll<T>({
    enabled,
    interval: null,
    refetchDeps: [key],
    queryFn: fetch,
    onError: (err) => Sentry.captureException(toError(err)),
  });
  return {
    data: poll.data ?? null,
    error: errorOf(poll.error),
    isLoading: enabled && poll.isLoading,
    refetch: poll.refetch,
  };
}

/** A playbook, its current version whole and its history. */
export function usePlaybook(key: string): Read<PlaybookDetail> {
  return useRead(`playbook:${key}`, key !== '', () => vnextApiClient.getPlaybook(key));
}

/** One version of a playbook, whole — `number` null reads nothing. */
export function usePlaybookVersion(key: string, number: number | null): Read<PlaybookVersion> {
  return useRead(`playbook-version:${key}:${number ?? ''}`, number !== null, () =>
    vnextApiClient.getPlaybookVersion(key, number ?? 0)
  );
}
